import { useCallback, useRef, useState, type SyntheticEvent } from 'react'

import { AccessibleText } from '@/components/AccessibleText'
import { Disclaimer } from '@/components/BlackFriday/Disclaimer'
import { Image } from '@/components/Image'

import {
  useSelectButtonEvent,
  useSelectFieldEvent,
  useSelectIconEvent,
} from '@/core/analytics/hooks'

import { DISCLAIMER_TEXTS } from '../Disclaimer/constants'

import { Modal } from '../Modal'

import type { ModalComponentProps, ModalContentProps } from '../Modal/types'

import { INTEGRITY_CHECK_FORM_FOOTER } from './constants'

import {
  Form,
  FormFooter,
  FormFooterVerticalDivider,
  FormInput,
  FormSubmitButton,
  FormWrapper,
} from './styles'
import { getModalContent } from './utils'

type ModalStateProps = ModalContentProps & Pick<ModalComponentProps, 'isOpen'>

export const LinkIntegrityCheckForm = () => {
  const [modalContent, setModalContent] = useState({} as ModalStateProps)
  const linkInputRef = useRef<HTMLInputElement>(null)
  const isRequestLoading = useRef<boolean>(false)

  const selectFieldEvent = useSelectFieldEvent({ itemId: 'premium_web_campo_meio_07' })
  const selectButtonEvent = useSelectButtonEvent({ itemId: 'premium_web_botao_meio_09' })

  const onClose = useCallback((event?: SyntheticEvent<Element, Event>) => {
    event?.preventDefault()
    setModalContent((oldState) => ({ ...oldState, isOpen: false }))
  }, [])

  const handleSubmit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault()
      const link = linkInputRef.current?.value

      if (!link || isRequestLoading.current) return
      isRequestLoading.current = true

      const formattedLink = link.trim()

      selectFieldEvent({ label: 'cole-o-link-aqui-verificador-site-suspeito' })
      selectButtonEvent({ label: 'verificar' })

      const modalContent = await getModalContent(formattedLink)
      setModalContent({ ...modalContent, isOpen: true })
      isRequestLoading.current = false
    },
    [selectFieldEvent, selectButtonEvent],
  )

  return (
    <div className="media-with-forms__inputs">
      <FormWrapper>
        <Form onSubmit={handleSubmit} noValidate>
          <FormInput ref={linkInputRef} id="link" name="link" placeholder="Link do site" />
          <FormSubmitButton type="submit" aria-label="Verificar se link é seguro">
            Verificar
          </FormSubmitButton>
          <Modal {...modalContent} onClose={onClose} />
        </Form>
        <FormFooterComponent />
      </FormWrapper>
    </div>
  )
}

const FormFooterComponent = () => {
  const selectIconEvent = useSelectIconEvent({ itemId: 'premium_web_icone_meio_08' })
  const handleSelectPartnerLogoEvent = useCallback(
    () => selectIconEvent({ label: 'logo-dfndr-security' }),
    [selectIconEvent],
  )

  return (
    <FormFooter>
      <AccessibleText
        id="lic-footer-partnership"
        text="Uma parceria"
        accessibleText="Uma parceria dfndr security e Serasa"
        light="solid"
      />
      <a
        className="media-with-forms__inputs__partner-logo"
        onClick={handleSelectPartnerLogoEvent}
        href={INTEGRITY_CHECK_FORM_FOOTER.partner.website}
        aria-label={INTEGRITY_CHECK_FORM_FOOTER.partner.label}
      >
        <Image alt="" {...INTEGRITY_CHECK_FORM_FOOTER.images.dfndr} />
      </a>
      <FormFooterVerticalDivider aria-hidden />
      <div className="media-with-forms__inputs__serasa-logo">
        <Image alt="" {...INTEGRITY_CHECK_FORM_FOOTER.images.serasa} />
      </div>
      <Disclaimer
        id="li-footer-disclaimer"
        text={DISCLAIMER_TEXTS.linkSecurityCheck.text}
        accessibleText={DISCLAIMER_TEXTS.linkSecurityCheck.accessibleText}
        light="solid"
        className="link-integrity__footer__disclaimer"
      />
    </FormFooter>
  )
}
